/**
 * @fileoverview Custom export builder (MOR-1048).
 *
 * The report-builder experience — compose, save, star, export to Excel —
 * generalised to any export surface. Pivot mode uses the same field-panel
 * UI as the timesheets builder (`CustomExportFieldPanel`); the spec still
 * runs client-side over the rows the surface already renders.
 *
 * Saved exports are per-user and keyed by the surface's dataset, so a
 * table composed on one job is offered on every job with that surface.
 *
 * @component
 * @example
 * <CustomExportBuilder datasets={datasets} defaultTitle="Assets" />
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { saveAs } from "file-saver";
import {
  BookmarkPlus,
  ChevronDown,
  ChevronRight,
  Download,
  Loader2,
  Plus,
  Save,
  Star,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { CustomExportSpec } from "@cvx/savedCustomExports/schema";

import { extractErrorMessage } from "@/utils/errors";
import { cn } from "@/utils/misc";
import { useActiveOrg } from "@/utils/org-context";
import { exportToExcel } from "@/utils/export";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import { Checkbox } from "@/ui/primitives/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/ui/composed/menus/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import { CustomExportFieldPanel } from "./CustomExportFieldPanel";
import {
  DEFAULT_CUSTOM_EXPORT_SPEC,
  cellText,
  distinctValues,
  measureLabel,
  runCustomExport,
  specIsRunnable,
  type CustomExportCell,
  type CustomExportDataset,
} from "./engine";

// Matches the pivot builder's card treatment on the dialog's white surface.
const CARD_CLASS =
  "rounded-xl border border-border bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] overflow-hidden";

const PREVIEW_ROW_LIMIT = 200;

/** Sentinel for "no selection" in optional Selects (Radix rejects ""). */
const NONE = "__none__";

function formatPreviewCell(cell: CustomExportCell): string {
  if (cell === null || cell === undefined || cell === "") return "—";
  if (typeof cell === "number") {
    return Number.isInteger(cell)
      ? cell.toLocaleString("en-AU")
      : cell.toLocaleString("en-AU", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
  }
  return cellText(cell);
}

export function CustomExportBuilder({
  datasets,
  defaultTitle,
}: {
  datasets: CustomExportDataset[];
  /** Title seed for the export filename, e.g. the surface's page title. */
  defaultTitle?: string;
}) {
  const { orgId } = useActiveOrg();
  const [datasetKey, setDatasetKey] = useState(datasets[0]?.key ?? "");
  const [spec, setSpec] = useState<CustomExportSpec>(
    DEFAULT_CUSTOM_EXPORT_SPEC,
  );
  const [title, setTitle] = useState(defaultTitle ?? "Custom export");
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saving, setSaving] = useState(false);
  const [fieldsOpen, setFieldsOpen] = useState(true);

  const dataset =
    datasets.find((d) => d.key === datasetKey) ?? datasets[0] ?? null;

  const patch = (p: Partial<CustomExportSpec>) =>
    setSpec((prev) => ({ ...prev, ...p }));

  const saved = useQuery(
    api.customExports.listSaved,
    orgId && dataset ? { orgId, datasetKey: dataset.key } : "skip",
  );
  const saveReport = useMutation(api.customExports.saveReport);
  const deleteReport = useMutation(api.customExports.deleteReport);
  const setFavourite = useMutation(api.customExports.setFavourite);

  const result = useMemo(
    () =>
      dataset && specIsRunnable(dataset, spec)
        ? runCustomExport(dataset, spec)
        : null,
    [dataset, spec],
  );

  const handleSave = async () => {
    if (!saveName.trim() || saving || !orgId || !dataset) return;
    setSaving(true);
    try {
      const name = saveName.trim();
      await saveReport({ orgId, datasetKey: dataset.key, name, spec });
      setTitle(name);
      toast.success(`Saved "${name}".`);
      setSaveOpen(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not save the export."));
    } finally {
      setSaving(false);
    }
  };

  const handleExport = () => {
    if (!result || !dataset) return;
    const blob = exportToExcel({
      title,
      sheets: [
        {
          name: dataset.label,
          headers: result.headers,
          rows: result.rows,
        },
      ],
    });
    const base = title.replace(/[^a-zA-Z0-9\s\-_]/g, "").replace(/\s+/g, "_");
    saveAs(blob, `${base || "Custom_export"}.xlsx`);
  };

  if (!dataset) {
    return (
      <p className="py-6 text-sm text-muted-foreground">
        Nothing to export yet — this view has no tabular data.
      </p>
    );
  }

  const savedSorted = [...(saved ?? [])].sort(
    (a, b) => Number(b.isFavourite ?? false) - Number(a.isFavourite ?? false),
  );

  const columnOn = (column: string) =>
    spec.columns.length === 0 || spec.columns.includes(column);

  const toggleColumn = (column: string) => {
    // An empty selection means "all", so the first untick materialises it.
    const current =
      spec.columns.length === 0 ? [...dataset.headers] : [...spec.columns];
    const next = current.includes(column)
      ? current.filter((c) => c !== column)
      : // Keep header order rather than click order.
        dataset.headers.filter((h) => current.includes(h) || h === column);
    patch({ columns: next.length === dataset.headers.length ? [] : next });
  };

  const fieldSummary = [
    spec.rows.join(" › ") + (spec.pivotColumn ? ` × ${spec.pivotColumn}` : ""),
    spec.measures.map((m) => measureLabel(m)).join(", "),
    spec.filters.length > 0 ? "filtered" : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {savedSorted.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">
            Saved exports:
          </span>
          {savedSorted.map((r) => (
            <span
              key={r._id}
              className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-background pl-2.5 text-xs"
            >
              <button
                type="button"
                className="py-1 hover:text-primary"
                onClick={() => {
                  setSpec(r.spec);
                  setTitle(r.name);
                  // Pre-filling the name makes Save an update in place.
                  setSaveName(r.name);
                }}
                title="Load this export — Save updates it in place"
              >
                {r.name}
              </button>
              <button
                type="button"
                className={cn(
                  "rounded-full p-1",
                  r.isFavourite
                    ? "text-amber-500"
                    : "text-muted-foreground hover:text-amber-500",
                )}
                onClick={() =>
                  setFavourite({
                    reportId: r._id,
                    isFavourite: !r.isFavourite,
                  }).catch((err: unknown) =>
                    toast.error(
                      extractErrorMessage(err, "Could not star the export."),
                    ),
                  )
                }
                aria-label={
                  r.isFavourite ? `Unstar ${r.name}` : `Star ${r.name}`
                }
              >
                <Star
                  className={cn("size-3", r.isFavourite && "fill-current")}
                />
              </button>
              <button
                type="button"
                className="rounded-full p-1 text-muted-foreground hover:text-destructive"
                onClick={() =>
                  deleteReport({ reportId: r._id }).catch((err: unknown) =>
                    toast.error(
                      extractErrorMessage(err, "Could not delete the export."),
                    ),
                  )
                }
                aria-label={`Delete ${r.name}`}
              >
                <Trash2 className="size-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className={cn(CARD_CLASS, "flex flex-col gap-3 p-4")}>
        <div className="flex flex-wrap items-end gap-3">
          {datasets.length > 1 && (
            <div className="flex flex-col gap-1">
              <Label className="text-xs">Data</Label>
              <Select
                value={dataset.key}
                onValueChange={(value) => {
                  setDatasetKey(value);
                  // Column names differ between datasets; start clean.
                  setSpec(DEFAULT_CUSTOM_EXPORT_SPEC);
                }}
              >
                <SelectTrigger className="h-8 w-52">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {datasets.map((d) => (
                    <SelectItem key={d.key} value={d.key}>
                      {d.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Layout</Label>
            <div className="flex items-center rounded-md border border-border">
              <Button
                variant={spec.mode === "table" ? "secondary" : "ghost"}
                size="sm"
                className="h-8 rounded-r-none"
                onClick={() => patch({ mode: "table" })}
              >
                Table
              </Button>
              <Button
                variant={spec.mode === "pivot" ? "secondary" : "ghost"}
                size="sm"
                className="h-8 rounded-l-none"
                onClick={() => patch({ mode: "pivot" })}
              >
                Pivot
              </Button>
            </div>
          </div>
          {spec.mode === "table" && (
            <>
              <div className="flex flex-col gap-1">
                <Label className="text-xs">Sort by</Label>
                <Select
                  value={spec.sortBy ?? NONE}
                  onValueChange={(value) =>
                    patch({ sortBy: value === NONE ? undefined : value })
                  }
                >
                  <SelectTrigger className="h-8 w-44">
                    <SelectValue placeholder="Source order" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NONE}>Source order</SelectItem>
                    {dataset.headers.map((h) => (
                      <SelectItem key={h} value={h}>
                        {h}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {spec.sortBy && (
                <div className="flex flex-col gap-1">
                  <Label className="text-xs">Direction</Label>
                  <Select
                    value={spec.sortDir ?? "asc"}
                    onValueChange={(value) =>
                      patch({ sortDir: value === "desc" ? "desc" : "asc" })
                    }
                  >
                    <SelectTrigger className="h-8 w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="asc">Ascending</SelectItem>
                      <SelectItem value="desc">Descending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </>
          )}
        </div>

        {spec.mode === "table" ? (
          <div className="border-t border-border/50 pt-3">
            <p className="mb-2 text-xs font-medium text-muted-foreground">
              Columns
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-1.5">
              {dataset.headers.map((h) => (
                <label key={h} className="flex items-center gap-1.5 text-xs">
                  <Checkbox
                    checked={columnOn(h)}
                    onCheckedChange={() => toggleColumn(h)}
                  />
                  {h}
                </label>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className="flex min-w-0 items-center gap-2 border-t border-border/50 pt-3">
              <Button
                variant="ghost"
                size="sm"
                className="-ml-2 h-7 gap-1 px-2"
                onClick={() => setFieldsOpen((prev) => !prev)}
                aria-expanded={fieldsOpen}
              >
                {fieldsOpen ? (
                  <ChevronDown className="size-4" />
                ) : (
                  <ChevronRight className="size-4" />
                )}
                Fields
              </Button>
              {!fieldsOpen && (
                <span className="min-w-0 truncate text-xs text-muted-foreground">
                  {fieldSummary}
                </span>
              )}
            </div>
            {fieldsOpen && (
              <>
                <CustomExportFieldPanel
                  dataset={dataset}
                  spec={spec}
                  onChange={patch}
                />
                <div className="flex flex-wrap items-center gap-3 border-t border-border/50 pt-3">
                  <label className="flex items-center gap-1.5 text-xs">
                    <Checkbox
                      checked={spec.showTotals}
                      onCheckedChange={(checked) =>
                        patch({ showTotals: checked === true })
                      }
                    />
                    Grand total row
                  </label>
                </div>
              </>
            )}
          </>
        )}

        {spec.mode === "table" && (
          <div className="border-t border-border/50 pt-3">
            <div className="mb-2 flex items-center gap-2">
              <p className="text-xs font-medium text-muted-foreground">
                Filters
              </p>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 gap-1 px-2 text-xs"
                onClick={() =>
                  patch({
                    filters: [
                      ...spec.filters,
                      {
                        column: dataset.headers[0],
                        negate: false,
                        values: [],
                      },
                    ],
                  })
                }
              >
                <Plus className="size-3" />
                Add filter
              </Button>
            </div>
            {spec.filters.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                No filters — every row is included.
              </p>
            ) : (
              <div className="flex flex-col gap-1.5">
                {spec.filters.map((f, i) => {
                  const options = distinctValues(dataset, f.column);
                  const setFilter = (
                    updated: Partial<CustomExportSpec["filters"][number]>,
                  ) =>
                    patch({
                      filters: spec.filters.map((x, j) =>
                        j === i ? { ...x, ...updated } : x,
                      ),
                    });
                  return (
                    <div
                      key={i}
                      className="flex flex-wrap items-center gap-1.5"
                    >
                      <Select
                        value={f.column}
                        onValueChange={(column) =>
                          setFilter({ column, values: [] })
                        }
                      >
                        <SelectTrigger className="h-8 w-44">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {dataset.headers.map((h) => (
                            <SelectItem key={h} value={h}>
                              {h}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={f.negate ? "not" : "is"}
                        onValueChange={(value) =>
                          setFilter({ negate: value === "not" })
                        }
                      >
                        <SelectTrigger className="h-8 w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="is">is any of</SelectItem>
                          <SelectItem value="not">is not</SelectItem>
                        </SelectContent>
                      </Select>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 gap-1.5 font-normal"
                          >
                            {f.values.length === 0
                              ? "Any value"
                              : f.values.length === 1
                                ? f.values[0] || "(blank)"
                                : `${f.values.length} selected`}
                            <ChevronDown className="size-3.5 text-muted-foreground" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="start"
                          className="max-h-72 w-64 overflow-y-auto"
                        >
                          {options.map((value) => (
                            <DropdownMenuCheckboxItem
                              key={value}
                              checked={f.values.includes(value)}
                              onCheckedChange={(checked) =>
                                setFilter({
                                  values: checked
                                    ? [...f.values, value]
                                    : f.values.filter((x) => x !== value),
                                })
                              }
                              onSelect={(e) => e.preventDefault()}
                            >
                              {value || "(blank)"}
                            </DropdownMenuCheckboxItem>
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                      <button
                        type="button"
                        className="rounded-full p-1 text-muted-foreground hover:text-destructive"
                        onClick={() =>
                          patch({
                            filters: spec.filters.filter((_, j) => j !== i),
                          })
                        }
                        aria-label="Remove filter"
                      >
                        <X className="size-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Result — exporting and saving act on this preview. */}
      <div className={cn(CARD_CLASS, "flex min-w-0 flex-col gap-3 p-4")}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-semibold">{title}</p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              disabled={!result || result.rows.length === 0}
              onClick={handleExport}
            >
              <Download className="size-4" />
              Export Excel
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              disabled={!orgId}
              onClick={() => setSaveOpen(true)}
            >
              <BookmarkPlus className="size-4" />
              Save
            </Button>
          </div>
        </div>
        {!result ? (
          <p className="py-6 text-sm text-muted-foreground">
            Pick at least one row field and one value to run the pivot.
          </p>
        ) : result.rows.length === 0 ? (
          <p className="py-6 text-sm text-muted-foreground">
            No rows match the current filters.
          </p>
        ) : (
          <div className="max-h-[50vh] overflow-auto rounded-lg border border-border/60">
            <table className="w-full min-w-max border-collapse text-xs">
              <thead className="sticky top-0 bg-muted/80 backdrop-blur">
                <tr>
                  {result.headers.map((h, i) => (
                    <th
                      key={i}
                      className="whitespace-nowrap border-b border-border/60 px-3 py-2 text-left font-medium"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.rows.slice(0, PREVIEW_ROW_LIMIT).map((row, ri) => (
                  <tr
                    key={ri}
                    className={cn(
                      "border-b border-border/40 last:border-0",
                      spec.mode === "pivot" &&
                        spec.showTotals &&
                        ri === result.rows.length - 1 &&
                        "font-semibold",
                    )}
                  >
                    {row.map((cell, ci) => (
                      <td
                        key={ci}
                        className={cn(
                          "whitespace-nowrap px-3 py-1.5",
                          typeof cell === "number" && "text-right tabular-nums",
                        )}
                      >
                        {formatPreviewCell(cell)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {result.rows.length > PREVIEW_ROW_LIMIT && (
              <p className="border-t border-border/40 px-3 py-1.5 text-[11px] text-muted-foreground">
                Showing {PREVIEW_ROW_LIMIT} of {result.rows.length} rows — the
                Excel export includes them all.
              </p>
            )}
          </div>
        )}
        {result && (
          <p className="text-[11px] text-muted-foreground">
            {result.sourceCount} source row{result.sourceCount === 1 ? "" : "s"}
            {spec.filters.some((f) => f.values.length > 0)
              ? " after filters"
              : ""}
            .
          </p>
        )}
      </div>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save this export</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <Label htmlFor="custom-export-name">Name</Label>
            <Input
              id="custom-export-name"
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              placeholder='e.g. "Secured creditors by state"'
              onKeyDown={(e) => {
                if (e.key === "Enter") void handleSave();
              }}
            />
            <p className="text-xs text-muted-foreground">
              Saved exports re-run over the live rows every time you open this
              view. Saving with an existing name updates that export.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={!saveName.trim() || saving}
              onClick={() => void handleSave()}
            >
              {saving ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Save className="size-4" />
              )}
              Save export
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
